// src/hooks/brandCustomization/useEmailCustomization.js
import { useState, useEffect } from 'react';
import emailCustomizationService from '../../helpers/emailCustomizationHelper';
import { defaultEmailTexts, defaultEmailStyles } from '../../utils/brandCustomization';
import Swal from 'sweetalert2';

/**
 * Hook para manejar la personalización de email templates
 * @param {string} marcaId - ID de la marca
 * @returns {object} Estado y funciones para manejar email customization
 */
export const useEmailCustomization = (marcaId) => {
    // Inicializar formData con valores por defecto para evitar estados vacíos
    const initializeFormData = () => {
        const templates = ['ticketCreation', 'ticketResend', 'addFranchise', 'addWorker', 'winback'];
        const initial = {};
        templates.forEach(templateName => {
            initial[templateName] = {
                greeting: defaultEmailTexts[templateName].greeting,
                mainMessage: defaultEmailTexts[templateName].mainMessage,
                ctaText: defaultEmailTexts[templateName].ctaText,
                closingMessage: defaultEmailTexts[templateName].closingMessage,
                footerText: defaultEmailTexts[templateName].footerText,
                isActive: true,
                styles: { ...defaultEmailStyles }
            };
        });
        return initial;
    };

    const [formData, setFormData] = useState(initializeFormData());
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState({});

    // Merge de estilos con defaults
    const mergeStyles = (customStyles) => {
        if (!customStyles) return defaultEmailStyles;
        return {
            headerBackgroundColor: customStyles.headerBackgroundColor || defaultEmailStyles.headerBackgroundColor,
            headerTextColor: customStyles.headerTextColor || defaultEmailStyles.headerTextColor,
            bodyBackgroundColor: customStyles.bodyBackgroundColor || defaultEmailStyles.bodyBackgroundColor,
            bodyTextColor: customStyles.bodyTextColor || defaultEmailStyles.bodyTextColor,
            buttonBackgroundColor: customStyles.buttonBackgroundColor || defaultEmailStyles.buttonBackgroundColor,
            buttonTextColor: customStyles.buttonTextColor || defaultEmailStyles.buttonTextColor,
            footerTextColor: customStyles.footerTextColor || defaultEmailStyles.footerTextColor,
            fontFamily: customStyles.fontFamily || defaultEmailStyles.fontFamily,
            fontSize: customStyles.fontSize || defaultEmailStyles.fontSize,
            borderRadius: customStyles.borderRadius || defaultEmailStyles.borderRadius
        };
    };

    // Cargar datos del backend
    const fetchData = async () => {
        if (!marcaId) return;

        setLoading(true);
        try {
            let customizationsData = { templates: [] };
            try {
                customizationsData = await emailCustomizationService.getCustomizations(marcaId);
            } catch (error) {
                if (error.status !== 404) {
                    console.warn('Error obteniendo personalizaciones:', error);
                }
            }

            // Organizar personalizaciones por template
            const customizationsMap = {};
            if (customizationsData && customizationsData.templates && Array.isArray(customizationsData.templates)) {
                customizationsData.templates.forEach(template => {
                    if (template && template.templateName) {
                        customizationsMap[template.templateName] = template;
                    }
                });
            }

            // Templates disponibles
            const templates = ['ticketCreation', 'ticketResend', 'addFranchise', 'addWorker', 'winback'];

            // Inicializar formData con datos del backend o defaults
            const initialFormData = {};
            templates.forEach(templateName => {
                initialFormData[templateName] = {
                    greeting: customizationsMap[templateName]?.texts?.greeting || defaultEmailTexts[templateName].greeting,
                    mainMessage: customizationsMap[templateName]?.texts?.mainMessage || defaultEmailTexts[templateName].mainMessage,
                    ctaText: customizationsMap[templateName]?.texts?.ctaText || defaultEmailTexts[templateName].ctaText,
                    closingMessage: customizationsMap[templateName]?.texts?.closingMessage || defaultEmailTexts[templateName].closingMessage,
                    footerText: customizationsMap[templateName]?.texts?.footerText || defaultEmailTexts[templateName].footerText,
                    isActive: customizationsMap[templateName]?.isActive !== false,
                    styles: mergeStyles(customizationsMap[templateName]?.styles)
                };
            });

            setFormData(initialFormData);
        } catch (error) {
            console.error('Error cargando personalizaciones de email:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudieron cargar las personalizaciones',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [marcaId]);

    // Actualizar un campo específico
    const updateField = (templateName, field, value) => {
        setFormData(prev => {
            // Si el campo es anidado (ej: 'styles.headerBackgroundColor')
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return {
                    ...prev,
                    [templateName]: {
                        ...prev[templateName],
                        [parent]: {
                            ...prev[templateName][parent],
                            [child]: value
                        }
                    }
                };
            }

            // Campo simple
            return {
                ...prev,
                [templateName]: {
                    ...prev[templateName],
                    [field]: value
                }
            };
        });
    };

    // Toggle activación
    const toggleActive = async (templateName) => {
        const currentActive = formData[templateName]?.isActive;
        const newActive = !currentActive;

        // Actualizar optimisticamente en UI
        updateField(templateName, 'isActive', newActive);

        try {
            await emailCustomizationService.updateCustomization(marcaId, templateName, {
                isActive: newActive
            });

            Swal.fire({
                title: newActive ? 'Personalización Activada' : 'Personalización Desactivada',
                text: newActive
                    ? 'Los emails usarán la personalización personalizada'
                    : 'Los emails usarán los textos por defecto',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            // Revertir en caso de error
            updateField(templateName, 'isActive', currentActive);

            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo actualizar el estado',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Guardar personalización
    const save = async (templateName) => {
        setIsSaving(prev => ({ ...prev, [templateName]: true }));

        try {
            const templateData = formData[templateName];
            const customizationData = {
                texts: {
                    greeting: templateData.greeting,
                    mainMessage: templateData.mainMessage,
                    ctaText: templateData.ctaText,
                    closingMessage: templateData.closingMessage,
                    footerText: templateData.footerText
                },
                styles: templateData.styles,
                isActive: templateData.isActive
            };

            // Intentar actualizar primero
            try {
                await emailCustomizationService.updateCustomization(marcaId, templateName, customizationData);
            } catch (updateError) {
                // Si no existe, crear
                if (updateError.status === 404) {
                    await emailCustomizationService.createCustomization(marcaId, [{
                        templateName,
                        ...customizationData
                    }]);
                } else {
                    throw updateError;
                }
            }

            Swal.fire({
                title: '¡Guardado!',
                text: 'La personalización se guardó correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            // Recargar datos
            await fetchData();
        } catch (error) {
            console.error('Error guardando personalización:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo guardar la personalización',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSaving(prev => ({ ...prev, [templateName]: false }));
        }
    };

    // Resetear a valores por defecto
    const reset = async (templateName) => {
        const result = await Swal.fire({
            title: '¿Resetear Personalización?',
            text: 'Se perderán todos los cambios personalizados',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, resetear',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            await emailCustomizationService.deleteCustomization(marcaId, templateName);

            Swal.fire({
                title: '¡Reseteado!',
                text: 'Se restauraron los valores por defecto',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            // Recargar datos
            await fetchData();
        } catch (error) {
            console.error('Error reseteando personalización:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo resetear la personalización',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Preview - Usa el backend igual que el original
    const preview = async (templateName, marca) => {
        try {
            const templateData = formData[templateName];

            // Asegurar que los estilos estén completos con valores por defecto
            // Usar los estilos del formulario si existen, sino valores por defecto
            const completeStyles = {
                headerBackgroundColor: (templateData.styles?.headerBackgroundColor && templateData.styles.headerBackgroundColor.trim()) || defaultEmailStyles.headerBackgroundColor,
                headerTextColor: (templateData.styles?.headerTextColor && templateData.styles.headerTextColor.trim()) || defaultEmailStyles.headerTextColor,
                bodyBackgroundColor: (templateData.styles?.bodyBackgroundColor && templateData.styles.bodyBackgroundColor.trim()) || defaultEmailStyles.bodyBackgroundColor,
                bodyTextColor: (templateData.styles?.bodyTextColor && templateData.styles.bodyTextColor.trim()) || defaultEmailStyles.bodyTextColor,
                buttonBackgroundColor: (templateData.styles?.buttonBackgroundColor && templateData.styles.buttonBackgroundColor.trim()) || defaultEmailStyles.buttonBackgroundColor,
                buttonTextColor: (templateData.styles?.buttonTextColor && templateData.styles.buttonTextColor.trim()) || defaultEmailStyles.buttonTextColor,
                footerTextColor: (templateData.styles?.footerTextColor && templateData.styles.footerTextColor.trim()) || defaultEmailStyles.footerTextColor,
                fontFamily: (templateData.styles?.fontFamily && templateData.styles.fontFamily.trim()) || defaultEmailStyles.fontFamily,
                fontSize: (templateData.styles?.fontSize && templateData.styles.fontSize.trim()) || defaultEmailStyles.fontSize,
                borderRadius: (templateData.styles?.borderRadius && templateData.styles.borderRadius.trim()) || defaultEmailStyles.borderRadius
            };

            // Formato esperado por el backend según la documentación (igual que el original)
            const previewData = {
                marcaId: String(marcaId), // Asegurar que sea string
                templateName: String(templateName), // Asegurar que sea string
                customization: {
                    templateName: String(templateName),
                    texts: {
                        // Usar el valor del formData si existe y no está vacío, sino usar el default
                        greeting: (templateData.greeting && templateData.greeting.trim()) || defaultEmailTexts[templateName].greeting,
                        mainMessage: (templateData.mainMessage && templateData.mainMessage.trim()) || defaultEmailTexts[templateName].mainMessage,
                        ctaText: (templateData.ctaText && templateData.ctaText.trim()) || defaultEmailTexts[templateName].ctaText,
                        closingMessage: (templateData.closingMessage && templateData.closingMessage.trim()) || defaultEmailTexts[templateName].closingMessage,
                        footerText: (templateData.footerText && templateData.footerText.trim()) || defaultEmailTexts[templateName].footerText
                    },
                    styles: completeStyles,
                    isActive: templateData.isActive
                },
                sampleVariables: {
                    nombre: 'Juan Pérez',
                    link: 'https://petitprime.com/ticket/example123',
                    brandName: marca?.nombre || 'Tu Marca',
                    brandSlogan: marca?.eslogan || 'Tu eslogan aquí',
                    logoUrl: marca?.logotipo?.cdnUrl || marca?.logotipo?.url || '',
                    supportEmail: 'soporte@petitprime.com',
                    // Añadir tiendaNombre para templates addFranchise y addWorker
                    ...(templateName === 'addFranchise' || templateName === 'addWorker' ? {
                        tiendaNombre: 'Tienda Centro'
                    } : {}),
                    // Añadir daysInactive y points para winback
                    ...(templateName === 'winback' ? {
                        daysInactive: '15',
                        points: '350'
                    } : {})
                }
            };

            const preview = await emailCustomizationService.previewEmail(previewData);
            return preview.html || preview;
        } catch (error) {
            console.error('Error generando preview:', error);
            throw error;
        }
    };

    return {
        formData,
        loading,
        isSaving,
        updateField,
        toggleActive,
        save,
        reset,
        preview,
        fetchData
    };
};

export default useEmailCustomization;

